import * as React from 'react';
import { useSelector } from 'react-redux';
import { UXFB_CLIENT_EVENTS_CONFIG_KEY, UXFB_EVENT_OPEN_OFFER_CARD } from 'shared/constants/uxfb';
import { isRentDailySelector } from 'shared/selectors/isRentDaily';
import { isNewbuildingFromDeveloperSelector, selectOffer } from 'shared/selectors/offerData';

import { useApplicationContext } from '../../containers/ApplicationContext';
import { getOpenOfferCardAttributes, sendUXSEvent, sendUXSEventWhenReady, TUxfbOfferType } from '../../utils/uxs';

/**
 * @param offerType вертикаль карточки для атрибута offer_type UXFB-события `open_offer_card`;
 * передаётся контейнером страницы явно (Flat/Newbuilding → flat, Suburban → suburban, Commercial → commercial,
 * DailyRent → suburban для dailyHouseRent и flat для остальных посуточных категорий).
 */
export function useCardUxfeedbackCampaign(offerType: TUxfbOfferType) {
  const { config } = useApplicationContext();
  const isDailyrent = useSelector(isRentDailySelector);
  const offer = useSelector(selectOffer);
  const fromDeveloper = useSelector(isNewbuildingFromDeveloperSelector);
  const hasSentOpenOfferCardRef = React.useRef(false);

  const campanyName = config.get<string>('uxfeedback.card.desktopCampanyName') || 'AudMonCardDesktop';
  const sendEventTimeout = config.get<number>('uxfeedback.card.sendEventTimeout') || 2_000;

  React.useEffect(() => {
    if (isDailyrent) {
      return;
    }

    setTimeout(() => {
      sendUXSEvent(campanyName);
    }, sendEventTimeout);
  }, [campanyName, sendEventTimeout, isDailyrent]);

  // Новое событие мониторинга (SEL-873): идёт параллельно с легаси AudMon*, ровно один раз на маунт.
  // Посутку не подавляем — шлём с is_daily=yes; правила показа кампаний настраиваются в админке UXFB.
  // dedupeKey обязателен: useRef не переживает ремаунт контейнера, а дубль события засчитывается
  // бэкендом UXFB как лишний «заход» — кампании «на 2-й заход» выезжают уже на первом.
  React.useEffect(() => {
    if (hasSentOpenOfferCardRef.current) {
      return;
    }

    if (config.get<boolean>(UXFB_CLIENT_EVENTS_CONFIG_KEY) === false) {
      return;
    }

    hasSentOpenOfferCardRef.current = true;

    sendUXSEventWhenReady(
      UXFB_EVENT_OPEN_OFFER_CARD,
      getOpenOfferCardAttributes({ offer, offerType, fromDeveloper, isDaily: isDailyrent }),
      {
        dedupeKey: UXFB_EVENT_OPEN_OFFER_CARD,
      },
    );
  }, [config, offer, offerType, fromDeveloper, isDailyrent]);
}
